import { useState, ReactNode } from 'react';
import { RefreshCw, RotateCcw, Trash2, Info } from 'lucide-react';
import { ViewMode } from '../contexts/ViewModeContext';

interface TrendCategoryCardProps {
  category: {
    id: string;
    name: string;
    query: string;
    fields: string[];
    threshold: number;
    status: string;
    totalChunks: number;
    completedChunks: number;
    totalMatches: number;
    lastScoredAt?: string;
    backfillStartedAt?: string;
    backfillCompletedAt?: string;
  };
  isSelected: boolean;
  isSyncing?: boolean;
  isBackfilling?: boolean;
  viewMode: ViewMode;
  onSelect: () => void;
  onDelete: () => void;
  onSync: () => void;
  onBackfill: () => void;
}

const fieldLabels: Record<string, string> = {
  factsEmbedding: 'Facts',
  causeOfActionEmbedding: 'Cause',
  reliefEmbedding: 'Relief'
};

const formatDuration = (startStr?: string, endStr?: string): string | null => {
  if (!startStr || !endStr) return null;
  const ms = new Date(endStr).getTime() - new Date(startStr).getTime();
  if (ms <= 0 || isNaN(ms)) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = (ms / 3600000).toFixed(1);
  return `${hrs}h`;
};

const formatRelativeTime = (dateStr?: string): string => {
  if (!dateStr) return 'never synced';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const StatusDot = ({ status, progress }: { status: string; progress: number }) => {
  switch (status) {
    case 'backfilling':
      return (
        <span className="inline-flex items-center gap-1 text-xs text-amber-600" title={`Backfilling ${progress}%`}>
          <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
        </span>
      );
    case 'ready':
      return (
        <span className="inline-flex items-center" title="Ready">
          <span className="w-2 h-2 bg-emerald-500 rounded-full" />
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center" title="Failed">
          <span className="w-2 h-2 bg-red-500 rounded-full" />
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center" title={status}>
          <span className="w-2 h-2 bg-gray-400 rounded-full" />
        </span>
      );
  }
};

const IconTooltip = ({ text, children }: { text: string; children: ReactNode }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative inline-block" onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg z-[9999] whitespace-normal text-center">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
        </div>
      )}
    </div>
  );
};

export const TrendCategoryCard = ({ category, isSelected, isSyncing, isBackfilling, viewMode, onSelect, onDelete, onSync, onBackfill }: TrendCategoryCardProps) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const progress = category.totalChunks > 0
    ? Math.round((category.completedChunks / category.totalChunks) * 100)
    : 0;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(`Delete category "${category.name}"? This will also delete all ${category.totalMatches.toLocaleString()} results.`)) {
      onDelete();
    }
  };

  const handleSync = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSync();
  };

  const handleBackfill = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(`Re-execute category "${category.name}" from scratch? This will rescore all documents.`)) {
      onBackfill();
    }
  };

  const tooltipContent = [
    `Fields: ${category.fields.map(f => fieldLabels[f] || f).join(', ')}`,
    `Synced: ${formatRelativeTime(category.lastScoredAt)}`,
    `Threshold: ≥ ${category.threshold.toFixed(2)}`,
    ...(formatDuration(category.backfillStartedAt, category.backfillCompletedAt)
      ? [`Backfill duration: ${formatDuration(category.backfillStartedAt, category.backfillCompletedAt)}`]
      : [])
  ].join('\n');

  return (
    <div
      data-component="TrendCategoryCard"
      onClick={onSelect}
      className={`flex-shrink-0 w-72 p-4 rounded-lg border cursor-pointer transition-all ${
        isSelected
          ? 'border-blue-500 bg-blue-50 shadow-md'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      {/* Row 1: title */}
      <h3 className="font-semibold text-gray-900 text-base leading-tight line-clamp-2 mb-2">
        {category.query}
      </h3>

      {/* Row 2: status + matches + info | action icons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusDot status={category.status} progress={progress} />
          <span className="text-sm text-gray-500">
            {category.totalMatches.toLocaleString()} matches
          </span>
          {viewMode === 'admin' && (
            <div
              className="relative inline-block"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
              {showTooltip && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg z-[9999] whitespace-pre-line">
                  {tooltipContent}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                </div>
              )}
            </div>
          )}
        </div>
        {viewMode === 'admin' && (
          <div className="flex items-center gap-1">
            {category.status === 'ready' && (
              <IconTooltip text="Score new documents against this category">
                <button
                  onClick={handleSync}
                  disabled={isSyncing}
                  className="p-1 text-gray-400 hover:text-blue-500 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                </button>
              </IconTooltip>
            )}
            <IconTooltip text="Re-score all documents from scratch">
              <button
                onClick={handleBackfill}
                disabled={isBackfilling || category.status === 'backfilling'}
                className="p-1 text-gray-400 hover:text-orange-500 transition-colors disabled:opacity-50"
              >
                <RotateCcw className={`w-4 h-4 ${isBackfilling ? 'animate-spin' : ''}`} />
              </button>
            </IconTooltip>
            <IconTooltip text="Delete this category and all its results">
              <button
                onClick={handleDelete}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </IconTooltip>
          </div>
        )}
      </div>
    </div>
  );
};
